import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import { getAuthConfig, getAuthUserId } from '../../../../data/auth/authSelectors'
import { RTKQueryHook, RTKQueryResultType } from '../../../../data/baseApi'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { useCurrentEpisodeForClient } from '../../../episodes/data/hooks/useCurrentEpisodeForClient'
import { useGetAssignmentsQuery } from '../assignmentsApi'

export const useAssignmentsData: RTKQueryHook<typeof useGetAssignmentsQuery> = (args, opts) => {
  const userId = useSelector(getAuthUserId)
  const clientId = useSelector(getSelectedClientId)
  const openClientAccessSupported = useSelector(getAuthConfig)?.openClientAccessSupported ?? false
  const { isDBTTeensEnabled } = useFlags()
  const { currentEpisode, loadingEpisodes } = useCurrentEpisodeForClient()

  const waitForOpenClientEpisode = openClientAccessSupported && isDBTTeensEnabled && loadingEpisodes

  // DBT Teens secondary providers are participants on the primary provider's episode; they do not own a parallel
  // episode for the same client. The active open-client episode is therefore the canonical assignment source.
  const useEpisodeProviderAssignments =
    openClientAccessSupported &&
    isDBTTeensEnabled &&
    currentEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens &&
    currentEpisode.provider_id !== userId

  const assignmentsQuery = useGetAssignmentsQuery(
    clientId && !waitForOpenClientEpisode
      ? {
          providerId: useEpisodeProviderAssignments ? currentEpisode.provider_id : userId,
          patientId: clientId,
          ...args,
        }
      : skipToken,
    opts,
  )

  const assignments = useMemo(() => {
    if (!useEpisodeProviderAssignments) {
      return assignmentsQuery.data
    }

    return assignmentsQuery.data?.filter((assignment: Assignment) => assignment.episode_id === currentEpisode?.id)
  }, [assignmentsQuery.data, currentEpisode?.id, useEpisodeProviderAssignments])

  return {
    ...assignmentsQuery,
    data: assignments,
    currentData: assignments,
  } as RTKQueryResultType<typeof useGetAssignmentsQuery>
}
